import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, orderBy, query, where } from "firebase/firestore";

import { db } from "../lib/firebase";
import { useCan } from "../lib/auth";
import { useCollection } from "../hooks/useCollection";
import { useDoc } from "../hooks/useDoc";
import { useCallableAction } from "../hooks/useCallableAction";
import type {
  SaveShipmentTrackingInput,
  SaveShipmentTrackingResult,
} from "../hooks/callableResults";
import {
  PageHeader, DataTable, EmptyState, Spinner,
  ErrorNote, Modal, Field, useToast,
} from "../components";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import { useProductionStageLabels } from "../hooks/useProductionStageLabels";
import { tl, dt } from "../lib/format";
import {
  CARGO_PROVIDERS, DEFAULT_CARGO_PROVIDER, buildTrackingUrl, resolveProvider, looksLikeHorozNumber,
} from "../lib/cargo";

/**
 * KARGO / SEVKİYAT — sevke hazır siparişlerin kuyruğu.
 *
 * DÜZELTİLEN ESKİ HATA (`configure.php:35-63` `save_shipment_info`):
 *     $existing[0] = $tracking_info;   // HER ZAMAN [0]
 * İkinci kargo bilgisi birincinin üzerine yazıyordu ve boş takip numarası
 * sessizce kabul ediliyordu — sipariş "kargolandı" görünüyor, müşteriye hiçbir
 * bilgi gitmiyordu. Artık takip numarası zorunlu, kayıt callable üzerinden
 * gidiyor ve Shopify'da fulfillment oluşturularak müşteriye e-posta atılıyor.
 */

/**
 * ★ SEVK KUYRUĞU BOŞ KALIYORDU — SORGU İŞ AKIŞININ OLMADIĞI YERE BAKIYORDU.
 *
 * Sorgu `["wc-packed","wc-kargoya-verildi"]` idi. Canlıda ölçüldü (14.627
 * sipariş, 2026-08-16):
 *   · `wc-packed`            :  0 sipariş  ← hiç doğmuyor (aşağıya bkz.)
 *   · `wc-kargoya-verildi`   :  1 sipariş  ← takip numarası ZATEN girilmiş
 *   · kuyrukta takip numarası OLMAYAN sipariş sayısı: 0/1
 *
 * Yani "sevk bekleyen" listesinde bugüne kadar TEK BİR sevk bekleyen sipariş
 * olmadı; listede yalnızca işi çoktan bitmiş siparişler görünüyordu ve her
 * satırdaki düğme "Kargo bilgisi gir" değil "Güncelle" yazıyordu. Ekran hata
 * vermiyordu — sorgu geçerliydi, sadece YANLIŞ KÜMEYE bakıyordu.
 *
 * SEBEP, statü sözlüğünün gerçeğinde:
 *   · `wc-packed`'in sözlükteki etiketi "Paketlenmiş" DEĞİL, "Üretimde-YK".
 *     Bu kimliği yazan tek yol `trendyol/sync.ts` (Trendyol "Invoiced");
 *     Shopify/elle açılan siparişte HİÇBİR kod yolu `wc-packed` yazmıyor.
 *   · Üretim tamamlandığında sipariş statüsü DEĞİŞMİYOR: `production/status.ts`
 *     yalnızca siparişin `productionStatus` alanını günceller, `erpStatus`
 *     `wc-uretimde` olarak kalır.
 *   · Stoktan çıkan (hızlı sevk) siparişler hiç üretime girmez; `wc-processing`
 *     ("Onaylandı") statüsünde kargolanmayı bekler. Canlıda bugün 11 sipariş.
 *
 * Kuyruk artık "kargolanabilir + kargolanmış" kümesidir. Ölçüt uydurma değil,
 * sunucudaki kuralın aynası: `orders/shipment.ts:NOT_SHIPPABLE` listesi
 * (tamamlanmış / iptal / iade / çöp) DIŞINDA kalan, ödemesi alınmış statüler.
 * `wc-on-hold` (Beklemede, ödenmemiş) ve `wc-failed` bilerek DIŞARIDA: onlar
 * sevk edilecek iş değil.
 */
const SHIPPING_STATUSES = [
  "wc-processing",       // Onaylandı — hızlı sevk / stoktan çıkan siparişler
  "wc-uretimde",         // Üretimde — üretim bitse de statü burada kalıyor
  "wc-packed",           // "Üretimde-YK" — Trendyol "Invoiced" karşılığı
  "wc-kargoya-verildi",  // Kargoya Verildi — numarayı düzeltmek/izlemek için
] as const;

/**
 * Tek sayfada okunacak en fazla sipariş.
 *
 * ★ SESSİZ KESME YOK: kuyruk bu sayıya DAYANIRSA ekran bunu SÖYLER. Eskiden
 * `limit(200)` sessizdi; kuyruk 200'ü geçtiğinde en eski siparişler listeden
 * düşüyor ve hiç kimse eksik olduğunu anlamıyordu (sorgu `createdAt desc`
 * sıralı, yani kesilen kayıtlar EN ESKİ, yani en acil olanlardır).
 */
const PAGE_LIMIT = 200;

// Kargo firmaları TEK kaynaktan (lib/cargo.ts) gelir.

interface OrderRow {
  id: string;
  orderName?: string;
  erpStatus?: string;
  erpStatusLabel?: string;
  productionStatus?: string;
  customerFullName?: string;
  createdAt?: unknown;
  totals?: { total?: number };
  /**
   * ★ İKİ AYRI ŞEMA. Webhook (`shopify/mappers.ts` ve Trendyol) `tracking.number`
   * / `tracking.company` yazar; ERP'nin kendi kargo formu `trackingInfo.trackingNumber`
   * yazar. Ekran yalnızca ikincisini okuyordu: Shopify ya da Trendyol tarafından
   * kargolanan sipariş listede "takip no girilmemiş" görünüyor, ekip aynı kargoyu
   * ikinci kez işlemeye kalkıyordu. (OrderDetail bunu zaten doğru yapıyordu.)
   */
  // `tracking` haritasının GERÇEK şeması: company / number / url / shippedAt.
  // (`trackingNumber` anahtarı buradan kaldırıldı — hiçbir yazan taraf üretmiyor.)
  tracking?: { number?: string; company?: string; url?: string };
  trackingInfo?: {
    trackingNumber?: string;
    provider?: string;
    company?: string;
    /** Shopify senkron damgası — bkz. functions/src/orders/shipment.ts. */
    shopifySync?: { ok?: boolean; state?: string; error?: string };
  };
}

/**
 * İlk BOŞ OLMAYAN dizeyi döndürür. `??` yerine bunun kullanılma sebebi için
 * aşağıdaki `_tracking` yorumuna bakın: sunucu boş dize yer tutucular yazıyor.
 */
function firstNonEmpty(...vals: Array<string | undefined | null>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v;
  }
  return "";
}

/* ==========================================================================
 * Otomatik UPS taraması — durum kartı
 * ======================================================================= */

interface SweepDoc {
  lastRunAt?: unknown;
  source?: "scheduled" | "manual";
  scanned?: number;
  updated?: number;
  delivered?: number;
  completed?: number;
  skipped?: number;
  failed?: number;
}

/**
 * ★ "OTOMATİK TAKİP ÇALIŞIYOR MU?" SORUSUNUN EKRANDAKİ CEVABI.
 *
 * Zamanlanmış tarama günde iki kez (08:00 / 20:00) teslim edilmemiş UPS
 * gönderilerini sorguluyor. Bu bugüne kadar yalnızca Cloud Functions
 * kayıtlarında görünüyordu — ofis personelinin oraya erişimi yok, tarama
 * sessizce ölse kimse fark etmezdi. Kart son taramanın ZAMANINI ve SONUCUNU
 * gösterir; beklemek istemeyen için "Şimdi tara" düğmesi aynı işi yapar.
 */
function UpsSweepPanel() {
  const toast = useToast();
  const sweep = useDoc<SweepDoc>("system/upsTrackSweep");
  const run = useCallableAction<Record<string, never>, {
    scanned: number; updated: number; delivered: number; completed: number;
    skipped: number; failed: number;
  }>("trackShipmentsNow");

  const s = sweep.data;

  const onRun = async () => {
    const out = await run.run({});
    if (!out) {
      toast.error(run.error ?? "Tarama yapılamadı.");
      return;
    }
    toast.success(
      `${out.scanned} gönderi sorgulandı.`,
      `${out.updated} güncellendi, ${out.delivered} teslim edildi` +
        (out.completed ? `, ${out.completed} sipariş tamamlandı` : "") +
        (out.failed ? `, ${out.failed} sorgulanamadı` : ""),
    );
  };

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <p className="font-medium text-gray-900">UPS otomatik takip</p>
          <p className="mt-0.5 text-gray-600">
            Teslim edilmemiş UPS gönderileri her gün 08:00 ve 20:00'de otomatik sorgulanır.
          </p>
          <p className="mt-1 text-gray-700">
            {sweep.loading
              ? "Son tarama okunuyor…"
              : s?.lastRunAt
                ? (
                    <>
                      Son tarama: <strong>{dt(s.lastRunAt)}</strong>
                      {s.source === "manual" ? " (elle)" : ""} — {s.scanned ?? 0} sorgulandı,{" "}
                      {s.updated ?? 0} güncellendi, {s.delivered ?? 0} teslim edildi
                      {s.completed ? `, ${s.completed} sipariş tamamlandı` : ""}
                      {s.failed ? `, ${s.failed} sorgulanamadı` : ""}.
                    </>
                  )
                : "Henüz otomatik tarama çalışmadı."}
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => void onRun()} disabled={run.pending}>
          {run.pending ? "Taranıyor…" : "Şimdi tara"}
        </button>
      </div>
      <ErrorNote error={sweep.error} className="mt-2" />
    </div>
  );
}

export function Shipping() {
  const can = useCan();
  const toast = useToast();
  const [target, setTarget] = useState<OrderRow | null>(null);
  const [provider, setProvider] = useState<string>(DEFAULT_CARGO_PROVIDER.label);
  const [customProvider, setCustomProvider] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notify, setNotify] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);

  const stages = useProductionStageLabels();
  const { data, loading, error } = useCollection<OrderRow>(
    () =>
      query(
        collection(db, "orders"),
        where("erpStatus", "in", [...SHIPPING_STATUSES]),
        orderBy("createdAt", "desc"),
        limit(PAGE_LIMIT),
      ),
    [],
  );

  /**
   * ★ SUNUCU İKİ FARKLI ŞEKİL DÖNDÜRÜYOR; TİP YALNIZCA BİRİNİ TANIYORDU.
   *
   * Normal yolda 9 anahtar gelir (`orders/shipment.ts:207`). Ama aynı takip
   * numarası ikinci kez gönderilirse sunucu ERKEN döner ve yalnızca ÜÇ anahtar
   * verir: `{ orderId, alreadySaved: true, shopify: null }` (`shipment.ts:81`).
   * O dalda Firestore'a yazılmaz, Shopify çağrılmaz, müşteriye bildirim GİTMEZ.
   *
   * Elle yazılmış eski tip alanların hepsini `?` ile isteğe bağlı ilan ettiği
   * için erken dönüşte hepsi `undefined` kalıyordu: `shopifySynced === false`
   * false çıkıyor, `shopifyState === "skipped"` de tutmuyor ve akış en alttaki
   * "kargo bilgisi kaydedildi, müşteriye bildirim gitti" YEŞİL bildirimine
   * düşüyordu. Ekran, olmamış bir şeyi olmuş gibi söylüyordu.
   *
   * Yeni tip `alreadySaved` üzerinden AYIRICI BİRLEŞİM: iki dalı da ele
   * almayan kod artık DERLENMEZ.
   */
  const save = useCallableAction<SaveShipmentTrackingInput, SaveShipmentTrackingResult>(
    "saveShipmentTracking",
  );

  const rows = useMemo(
    () =>
      data
        .map((o) => ({
        ...o,
        /**
         * ★ `??` DEĞİL `firstNonEmpty`.
         *
         * Sipariş webhook'u (`shopify/handlers/orders.ts`) her YENİ siparişe
         * BOŞ bir yer tutucu yazıyor: `tracking: { company:"", number:"", url:"",
         * shippedAt:null }`. `??` yalnızca null/undefined'da bir sonrakine
         * geçtiği için `""` değeri zinciri BURADA KESİYORDU.
         *
         * Canlıda ölçüldü: `trackingInfo` taşıyan 3 siparişin 3'ünde de
         * `tracking.number === ""` var. Yani ERP kargo formundan kaydedilen
         * gerçek takip numarası (`trackingInfo.trackingNumber`) bu boş yer
         * tutucu tarafından gölgeleniyor, sevk kuyruğunda üçü de "takip no
         * girilmemiş" görünüyordu — üstteki yorumun düzelttiğini iddia ettiği
         * hatanın ta kendisi, ters yönden.
         *
         * `tracking.trackingNumber` ayağı da kaldırıldı: `tracking` haritasında
         * böyle bir anahtar HİÇ YOK (şema `company/number/url/shippedAt`).
         */
        _tracking: firstNonEmpty(o.trackingInfo?.trackingNumber, o.tracking?.number),
        /** Kargo firması: iki şemada da farklı adla duruyor. */
        // Kayıtlı ad yer tutucu ("Özel Sağlayıcı") olabilir; numara `1Z…` ise
        // gönderi UPS'tir ve listede öyle görünmelidir.
        _carrier: (() => {
          const stored = firstNonEmpty(
            o.trackingInfo?.company,
            o.trackingInfo?.provider,
            o.tracking?.company,
          );
          const no = firstNonEmpty(o.trackingInfo?.trackingNumber, o.tracking?.number);
          return resolveProvider(stored, no) || stored;
        })(),
        // Takip numarası var ama Shopify'a yazılamamış: kuyrukta "tamam" gibi
        // görünüyordu, oysa müşteriye hiçbir bildirim gitmemiş.
        _syncFailed:
          o.trackingInfo?.shopifySync?.ok === false &&
          o.trackingInfo.shopifySync.state !== "skipped",
        }))
        /**
         * ★ SEVK BEKLEYEN ÖNCE. Kuyruk artık kargolanmışları da içerdiği için
         * (numarayı düzeltmek ve takip etmek gerekiyor) salt tarih sırası
         * yapılacak işi bitmiş işin altına gömüyordu. Depo sabah bu ekrana
         * bakıp ilk satırdan başlayabilmeli.
         *
         * Tarih sırası KORUNUR: sorgu zaten `createdAt desc` döndürüyor ve
         * `Array.prototype.sort` kararlıdır (ES2019), yani grup İÇİNDE sıra
         * bozulmaz.
         */
        .sort((a, b) => Number(Boolean(a._tracking)) - Number(Boolean(b._tracking))),
    [data],
  );

  /**
   * Sonuç kümesi üst sınıra DAYANDIYSA liste eksiktir. Sessiz kesme yok:
   * ekran bunu söyler, aksi hâlde en eski (= en acil) siparişler görünmeden
   * kaybolur.
   */
  const truncated = data.length >= PAGE_LIMIT;

  function open(o: OrderRow) {
    setTarget(o);
    setProvider(DEFAULT_CARGO_PROVIDER.label);
    setCustomProvider("");
    setTrackingNumber("");
    setNotify(true);
    setLocalError(null);
  }

  async function submit() {
    if (!target) return;
    const num = trackingNumber.trim();
    if (!num) {
      // Eski sistem boş takip numarasını sessizce kabul ediyordu.
      setLocalError("Takip numarası zorunludur.");
      return;
    }
    if (provider === "" && !customProvider.trim()) {
      setLocalError("Kargo firması seçin veya adını yazın.");
      return;
    }

    const res = await save.run({
      orderId: target.id,
      provider: provider || customProvider.trim(),
      ...(provider === "" ? { customProvider: customProvider.trim() } : {}),
      trackingNumber: num,
      // Takip bağlantısını firma kalıbından üretiyoruz; Shopify'ın müşteriye
      // gönderdiği e-postada tıklanabilir link çıksın diye.
      ...(buildTrackingUrl(provider, num) ? { trackingUrl: buildTrackingUrl(provider, num)! } : {}),
      notifyCustomer: notify,
    });

    if (!res) return; // Hata mesajı `save.error` içinde, modal açık kalır.

    const label = target.orderName ?? target.id;
    setTarget(null);

    /**
     * ★ ÖNCE ERKEN DÖNÜŞ DALI. Aynı takip numarası tekrar gönderildiğinde
     * sunucu mükerrer fulfillment açmamak için hiçbir şey yapmadan döner.
     * Bu bir HATA değil ama BAŞARI da değil: kullanıcıya "gitti" denemez.
     */
    if (res.alreadySaved) {
      // ★ Statü YİNE DE ilerlemiş olabilir: Trendyol taraması takip numarasını
      // paket oluştuğu anda yazdığı için sipariş numarası dolu hâlde
      // "Onaylandı"da bekleyebiliyor. Bu durumda kullanıcıya "hiçbir şey
      // olmadı" demek yanlış olurdu.
      toast.info(
        res.movedToShipped
          ? `${label}: takip numarası zaten kayıtlıydı; sipariş "Kargoya Verildi"ye alındı.`
          : `${label}: bu takip numarası zaten kayıtlı, yeniden gönderilmedi.`,
        "Mükerrer kargo kaydı açılmaması için numara yeniden yazılmadı; " +
          "Shopify'a YENİ bir kayıt gitmedi, müşteriye ikinci bir bildirim ÇIKMADI. " +
          "Numarayı gerçekten yenilemek istiyorsanız farklı bir takip numarası girin.",
      );
      return;
    }

    /**
     * Buradan sonrası 9 anahtarlı normal şekil; ayırıcı birleşim sayesinde
     * alanların DOLU olduğu derleyici tarafından garanti edilir.
     * `shopifyState` üç değerlidir: "yazılamadı" ile "yazılmaz" AYNI ŞEY DEĞİL.
     */
    if (res.shopifyState === "failed") {
      toast.error(
        `${label}: takip numarası kaydedildi ama Shopify'a yazılamadı — müşteriye bildirim GİTMEDİ.`,
        res.shopifyError ?? res.message ?? undefined,
      );
      return;
    }
    if (res.shopifyState === "skipped") {
      // Trendyol / göç edilmiş sipariş: Shopify'da karşılığı yok, denenmeyecek.
      toast.info(`${label}: takip numarası kaydedildi.`, res.message ?? undefined);
      return;
    }
    toast.success(
      `${label}: kargo bilgisi kaydedildi, müşteriye bildirim gitti.`,
      res.stockDeductedItems > 0
        ? `${res.stockDeductedItems} kalem mamul stoğundan düşüldü.`
        : undefined,
    );
  }

  return (
    <PageHeader
      title="Kargo / Sevkiyat"
      // "Paketlenmiş" denmiyor: sözlükte öyle bir sipariş statüsü YOK
      // (`wc-packed`'in etiketi "Üretimde-YK"). Ekranın gerçekte gösterdiği
      // küme, kapanmamış ve iptal edilmemiş siparişlerdir.
      description="Kargolanmayı bekleyen (Onaylandı / Üretimde) ve kargoya verilmiş siparişler. Takip numarası girildiğinde sipariş 'Kargoya Verildi'ye geçer, Shopify siparişlerinde kargo kaydı oluşur ve müşteriye bildirim gider."
    >
      <ErrorNote error={error} className="mb-4" />

      {truncated ? (
        <div
          role="status"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          Kuyruk {PAGE_LIMIT} siparişte kesildi; <strong>daha eski siparişler bu listede
          görünmüyor</strong>. Liste en yeniden eskiye sıralı olduğu için eksik olanlar
          en eski, yani en acil kayıtlardır. Bekleyen siparişleri Siparişler ekranından
          statüye göre süzerek kapatın.
        </div>
      ) : null}

      <UpsSweepPanel />

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Sevk bekleyen sipariş yok"
          description="Ödemesi alınmış, henüz tamamlanmamış siparişler burada listelenir. Liste boşsa açık sipariş kalmamıştır."
        />
      ) : (
        <DataTable
          caption="Sevk kuyruğu"
          rowKey={(r) => r.id}
          rows={rows}
          columns={[
            {
              key: "order",
              header: "Sipariş",
              render: (r) => (
                <span>
                  <Link
                    to={`/orders/${r.id}`}
                    className="font-medium text-primary-700 hover:underline"
                  >
                    {r.orderName ?? r.id}
                  </Link>
                  {/* Telefonda gizlenen "Müşteri" ve "Tutar" burada görünür. */}
                  <span className="block text-xs text-gray-600 sm:hidden">
                    {r.customerFullName ?? "—"} · {tl(r.totals?.total ?? null)}
                  </span>
                </span>
              ),
            },
            {
              key: "customer",
              header: "Müşteri",
              // ★ TELEFON: 7 sütun 686px yer istiyordu (343px kap).
              hideOnMobile: true,
              render: (r) => r.customerFullName ?? "—",
            },
            {
              key: "status",
              header: "Statü",
              render: (r) => (
                <OrderStatusBadge
                  erpStatus={r.erpStatus}
                  erpStatusLabel={r.erpStatusLabel}
                  productionStatus={r.productionStatus}
                  stages={stages}
                />
              ),
            },
            {
              key: "total",
              header: "Tutar",
              numeric: true,
              hideOnMobile: true,
              render: (r) => tl(r.totals?.total ?? null),
            },
            { key: "date", header: "Tarih", hideOnMobile: true, render: (r) => dt(r.createdAt) },
            {
              key: "tracking",
              header: "Takip No",
              render: (r) =>
                r._tracking ? (
                  <span className="tabular-nums">
                    {r._tracking}
                    {/* Kargo firması eskiden hiç gösterilmiyordu; hangi firmaya
                        verildiği yalnızca sipariş detayına girilince görülüyordu. */}
                    {r._carrier ? (
                      <span className="ml-2 text-xs text-gray-600">{r._carrier}</span>
                    ) : null}
                    {r._syncFailed ? (
                      <Link
                        to={`/orders/${r.id}`}
                        className="ml-2 whitespace-nowrap rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-800 hover:underline"
                        title="Takip numarası Shopify'a yazılamadı; müşteriye bildirim gitmedi."
                      >
                        Shopify'a gitmedi
                      </Link>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-amber-700">girilmemiş</span>
                ),
            },
            {
              key: "action",
              header: "",
              render: (r) =>
                can.isStaff ? (
                  <button type="button" onClick={() => open(r)} className="btn btn-primary">
                    {r._tracking ? "Güncelle" : "Kargo bilgisi gir"}
                  </button>
                ) : null,
            },
          ]}
        />
      )}

      <Modal
        open={target !== null}
        onClose={() => setTarget(null)}
        title={`Kargo bilgisi — ${target?.orderName ?? target?.id ?? ""}`}
      >
        <div className="space-y-4">
          <ErrorNote error={localError ?? save.error} />

          <Field label="Kargo firması" required>
            <select
              className="input"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {CARGO_PROVIDERS.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label}
                </option>
              ))}
              <option value="">Diğer (elle yaz)</option>
            </select>
          </Field>

          {provider === "" ? (
            <Field label="Firma adı" required>
              <input
                className="input"
                value={customProvider}
                onChange={(e) => setCustomProvider(e.target.value)}
                maxLength={120}
              />
            </Field>
          ) : null}

          <Field label="Takip numarası" required hint="Boş bırakılamaz.">
            <input
              className="input tabular-nums"
              value={trackingNumber}
              onChange={(e) => {
                const v = e.target.value;
                setTrackingNumber(v);
                // M… numara Horoz'dur; firma UPS/özel bırakıldıysa kendiliğinden geçer.
                if (looksLikeHorozNumber(v) && provider !== "Horoz Lojistik") setProvider("Horoz Lojistik");
              }}
              maxLength={64}
              autoComplete="off"
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="rounded border-gray-300"
            />
            Müşteriye kargo e-postası gönder (Shopify)
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setTarget(null)} className="btn bg-white text-gray-700 ring-1 ring-inset ring-gray-300">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={save.loading}
              className="btn btn-primary"
            >
              {save.loading ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      </Modal>
    </PageHeader>
  );
}

export default Shipping;
